import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AcademicCapIcon, UserGroupIcon, CurrencyDollarIcon, BookOpenIcon, ClipboardDocumentListIcon, CalendarIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import StatCard from '../../components/StatCard';
import { StatusBadge, CefrBadge } from '../../components/Badge';
import api from '../../utils/api';
import CurrencyConverter from '../../components/CurrencyConverter';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', error:'#EF4444', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB' };
const PKG_COLORS = [C.teal, '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#64748B'];

export default function AdminHome() {
  const [data, setData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [hwStats, setHwStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [dash, stud, hw] = await Promise.all([
          api.get('/api/admin/dashboard'),
          api.get('/api/admin/students'),
          api.get('/api/admin/homework/stats').catch(() => ({ data: null })),
        ]);
        setData(dash.data);
        setTenant(dash.data?.tenant);
        setStudents(stud.data?.students || stud.data || []);
        setHwStats(hw.data);
      } catch { setData({}); }
      finally { setLoading(false); }
    })();
  }, []);

  // Compute real package distribution from actual students
  const packageCounts = {};
  students.forEach(s => {
    const pkg = s.package || 'Unassigned';
    packageCounts[pkg] = (packageCounts[pkg] || 0) + 1;
  });
  const packageData = Object.entries(packageCounts).map(([name, value]) => ({ name, value }));

  // Compute real CEFR breakdown
  const cefrCounts = {};
  students.forEach(s => {
    const lvl = s.cefr_level || 'A1';
    cefrCounts[lvl] = (cefrCounts[lvl] || 0) + 1;
  });
  const cefrData = ['A1','A2','B1','B2','C1','C2'].map(l => ({ level: l, count: cefrCounts[l] || 0 })).filter(d => d.count > 0);

  const studentCount = data?.student_count ?? students.length;
  const tutorCount = data?.tutor_count ?? 0;
  const monthlyRevenue = data?.monthly_revenue ?? 0;

  // Real tutor capacity: students per tutor (capped at 100%)
  const maxStudentsPerTutor = 15;
  const capacityPct = tutorCount > 0
    ? Math.min(100, Math.round((studentCount / (tutorCount * maxStudentsPerTutor)) * 100))
    : (studentCount > 0 ? 100 : 0);
  const capColor = capacityPct < 60 ? C.success : capacityPct < 80 ? C.warning : C.error;

  const isEmpty = !loading && studentCount === 0 && tutorCount === 0;

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name || tenant?.name} tenantTier={tenant?.tier}>

      {/* Stat Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16, marginBottom:24 }}>
        <StatCard title="Active Students" value={loading ? null : studentCount} icon={AcademicCapIcon} color={C.teal} loading={loading} />
        <StatCard title="Active Tutors" value={loading ? null : tutorCount} icon={UserGroupIcon} color={C.success} loading={loading} />
        <StatCard title="This Month Revenue" value={loading ? null : monthlyRevenue} prefix="$" icon={CurrencyDollarIcon} color={C.success} loading={loading} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16, marginBottom:24 }}>
        <StatCard title="Lessons Today" value={loading ? null : (data?.lessons_today ?? 0)} icon={BookOpenIcon} color="#8B5CF6" loading={loading} />
        <StatCard title="Homework Completion" value={loading || !hwStats ? null : (hwStats.completion_rate || 0) + '%'} icon={ClipboardDocumentListIcon} color={C.teal} loading={loading} />
        <StatCard title="Attendance Today" value={loading || !hwStats ? null : (hwStats.attendance_today || 0)} icon={CalendarIcon} color={C.success} loading={loading} />
      </div>

      {/* Currency converter */}
      <div style={{ marginBottom:24 }}>
        <CurrencyConverter accent={C.teal} />
      </div>

      {/* Homework strip */}
      {hwStats && !isEmpty && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16, marginBottom:24 }}>
          <div className="card" style={{ padding:20, borderLeft:'4px solid #F59E0B' }}>
            <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Overdue Homework</div>
            <div style={{ fontSize:28, fontWeight:800, color:'#D97706' }}>{hwStats.overdue_count || 0}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>students haven't submitted</div>
          </div>
          <div className="card" style={{ padding:20, borderLeft:'4px solid #10B981' }}>
            <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Avg Score (this week)</div>
            <div style={{ fontSize:28, fontWeight:800, color:'#059669' }}>{hwStats.avg_score_week || 0}%</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>across all submissions</div>
          </div>
          <div className="card" style={{ padding:20, borderLeft:'4px solid #00B4D8' }}>
            <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Assigned This Week</div>
            <div style={{ fontSize:28, fontWeight:800, color:'#00B4D8' }}>{hwStats.total_assigned_week || 0}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>homework assignments</div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="card" style={{ padding:48, textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:56, marginBottom:16 }}>🏫</div>
          <h2 style={{ margin:'0 0 8px', fontSize:20, fontWeight:700, color:C.dark }}>Welcome to your Partner Dashboard</h2>
          <p style={{ color:C.grey, fontSize:15, margin:'0 0 28px', maxWidth:480, marginLeft:'auto', marginRight:'auto', lineHeight:1.6 }}>
            Your school is all set. Start by sharing your registration link with students and your tutor application page with teachers.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/register" style={{ padding:'12px 24px', background:C.teal, color:'#fff', borderRadius:8, fontWeight:700, fontSize:14, textDecoration:'none' }}>
              📋 Student Registration Page ↗
            </Link>
            <Link to="/admin/tutors" style={{ padding:'12px 24px', background:'#F3F4F6', color:C.dark, borderRadius:8, fontWeight:700, fontSize:14, textDecoration:'none' }}>
              👩‍🏫 Manage Tutors
            </Link>
          </div>
        </div>
      )}

      {/* Charts — only show when there's real data */}
      {!isEmpty && !loading && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:24 }}>
          {/* Package Distribution */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Students by Package</h3>
            {packageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={packageData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value">
                    {packageData.map((_, i) => <Cell key={i} fill={PKG_COLORS[i % PKG_COLORS.length]} />)}
                  </Pie>
                  <Legend iconType="circle" iconSize={8} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.grey, flexDirection:'column', gap:8 }}>
                <div style={{ fontSize:36 }}>📦</div>
                <div style={{ fontSize:14 }}>No packages assigned yet</div>
              </div>
            )}
          </div>

          {/* CEFR Breakdown */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Students by CEFR Level</h3>
            {cefrData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={cefrData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="level" tick={{ fontSize:12 }} />
                  <YAxis tick={{ fontSize:12 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Students" fill={C.teal} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height:200, display:'flex', alignItems:'center', justifyContent:'center', color:C.grey, flexDirection:'column', gap:8 }}>
                <div style={{ fontSize:36 }}>📊</div>
                <div style={{ fontSize:14 }}>No level data yet</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Revenue & Capacity */}
      {!isEmpty && !loading && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:24 }}>
          {/* Revenue */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 6px', fontSize:15, fontWeight:700 }}>Confirmed Revenue</h3>
            <div style={{ fontSize:12, color:C.grey, marginBottom:16 }}>Across course, mock test &amp; extra-session payments</div>
            {(() => {
              const rev = data?.revenue || {};
              const periods = [
                ['This month', rev.this_month],
                ['Last month', rev.last_month],
                ['This year',  rev.this_year],
                ['Last year',  rev.last_year],
                ['All time',   rev.all_time],
              ];
              const any = periods.some(([, v]) => (v || 0) > 0);
              if (!any) return (
                <div style={{ padding:'32px 0', textAlign:'center', color:C.grey }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>💰</div>
                  <div style={{ fontSize:14 }}>Revenue will appear once payments are confirmed</div>
                </div>
              );
              return periods.map(([label, value], i) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'11px 0', borderBottom: i < periods.length-1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize:13, color: label==='All time' ? C.navy : C.grey, fontWeight: label==='All time' ? 700 : 500 }}>{label}</span>
                  <span style={{ fontWeight: label==='All time' ? 800 : 700, fontSize: label==='All time' ? 17 : 14, color:C.success }}>${Number(value || 0).toLocaleString()}</span>
                </div>
              ));
            })()}
          </div>

          {/* Tutor Capacity */}
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Tutor Capacity</h3>
            {tutorCount > 0 ? (
              <>
                <div style={{ textAlign:'center', marginBottom:16 }}>
                  <div style={{ fontSize:48, fontWeight:800, color:capColor, lineHeight:1 }}>{capacityPct}%</div>
                  <div style={{ fontSize:13, color:C.grey, marginTop:4 }}>Current utilisation</div>
                </div>
                <div style={{ height:12, background:'#E5E7EB', borderRadius:6, marginBottom:16, overflow:'hidden' }}>
                  <div style={{ height:12, borderRadius:6, background:capColor, width:`${capacityPct}%`, transition:'width 0.5s' }} />
                </div>
                <div style={{ fontSize:14 }}>
                  <span style={{ color:C.grey }}>Students: </span>
                  <span style={{ fontWeight:700 }}>{studentCount}</span>
                  <span style={{ color:C.grey, margin:'0 8px' }}>·</span>
                  <span style={{ color:C.grey }}>Tutors: </span>
                  <span style={{ fontWeight:700 }}>{tutorCount}</span>
                </div>
              </>
            ) : (
              <div style={{ padding:'32px 0', textAlign:'center', color:C.grey }}>
                <div style={{ fontSize:32, marginBottom:8 }}>👩‍🏫</div>
                <div style={{ fontSize:14, marginBottom:16 }}>No tutors yet — recruit your first tutor</div>
                <Link to="/admin/tutors" style={{ padding:'10px 20px', background:C.teal, color:'#fff', borderRadius:8, fontWeight:600, fontSize:13, textDecoration:'none' }}>
                  Manage Tutors →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Students */}
      <div className="card" style={{ overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Recent Students</h3>
          <Link to="/admin/students" style={{ fontSize:13, color:C.teal, fontWeight:600, textDecoration:'none' }}>View All →</Link>
        </div>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:C.grey }}>Loading…</div>
        ) : students.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:C.grey }}>
            <UserPlusIcon style={{ width:40, height:40, margin:'0 auto 12px', color:'#D1D5DB' }} />
            <div style={{ fontSize:15, fontWeight:600, color:'#374151', marginBottom:4 }}>No students yet</div>
            <div style={{ fontSize:13 }}>Students will appear here once they register and are approved.</div>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F9FAFB' }}>
              {['Name','Package','Level','Joined','Status'].map(h => (
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {students.slice(0, 8).map((s, i) => (
                <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:'50%', background:C.teal+'18', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:C.teal, fontSize:13, flexShrink:0 }}>
                        {(s.first_name?.[0] || s.full_name?.[0] || 'S').toUpperCase()}
                      </div>
                      <span style={{ fontWeight:600, fontSize:14 }}>{s.full_name || (s.first_name + ' ' + s.last_name)}</span>
                    </div>
                  </td>
                  <td style={{ padding:'12px 16px', fontSize:13, color:C.grey }}>{s.package || s.package_name || '—'}</td>
                  <td style={{ padding:'12px 16px' }}><CefrBadge level={s.cefr_level || '—'} /></td>
                  <td style={{ padding:'12px 16px', fontSize:13, color:C.grey }}>
                    {s.created_at || s.joined_at ? new Date(s.created_at || s.joined_at).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td style={{ padding:'12px 16px' }}><StatusBadge status={s.account_status === 'deleted' ? 'Deleted' : s.account_status === 'blocked' ? 'Blocked' : s.account_status === 'suspended' ? 'Suspended' : s.is_active ? 'Active' : 'Pending'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
